import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { COPILOT_SESSION_METRIC_NAMES, type CopilotToolSummary, type ReportingCoverage } from '@ace-tracker/shared';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { METRIC_INFO } from '../metricInfo';
import { RepositoryBreakdownTable } from './RepositoryBreakdownTable';
import { ChartCard } from './ChartCard';
import { ContextWeightChart } from './ContextWeightChart';
import { CostEfficiencyChart } from './CostEfficiencyChart';
import { DrillDownTable } from './DrillDownTable';
import { IndicativeCostChart } from './IndicativeCostChart';
import { EfficiencyGauge } from './EfficiencyGauge';
import { KpiCard } from './KpiCard';

/** The Copilot-specific body of the dashboard — headline KPIs, trends, and drill-down for CopilotToolSummary. */
export function CopilotDashboardBody({
  toolData,
  developersCount,
  coverage,
  formatCurrency,
  SectionHeading,
}: {
  toolData: CopilotToolSummary;
  developersCount: number;
  coverage: ReportingCoverage;
  formatCurrency: (value: number, currency: string) => string;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const sessionActivity = summarizeCopilotSessionActivity(toolData.timeSeries);

  return (
    <>
      <section>
        <SectionHeading title="Headline metrics" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <KpiCard
            label="Legacy AIU estimate"
            value={formatCurrency(toolData.premiumQuota.totalIndicativeCost, toolData.premiumQuota.currency)}
            meta={`Recorded AIU at ${formatCurrency(toolData.premiumQuota.indicativeCostPerAiu, toolData.premiumQuota.currency)} each; not GitHub AI-credit billing`}
            flag="Not billing"
            info={METRIC_INFO.indicativeCost}
            hero
            accentClassName="bg-primary-blue"
          />
          <KpiCard
            label="Recorded AIU (legacy unit)"
            value={toolData.premiumQuota.totalAiu.toFixed(2)}
            unit="AIU"
            meta="Measured across all models in range"
            info={METRIC_INFO.premiumQuota}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active developers"
            value={String(developersCount)}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices reporting`}
            info={METRIC_INFO.activeDevelopers}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Tokens processed"
            value={(toolData.contextWeight.inputTokens + toolData.contextWeight.outputTokens).toLocaleString()}
            meta={`${toolData.contextWeight.ratio > 0 ? `${toolData.contextWeight.ratio}:1` : '—'} input:output ratio`}
            info={METRIC_INFO.tokensProcessed}
            accentClassName="bg-info"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EfficiencyGauge
            label="Chat success rate"
            value={toolData.chatSuccessRate.status === 'ok' ? (toolData.chatSuccessRate.ratePercent ?? null) : null}
            valueLabel={toolData.chatSuccessRate.status === 'ok' ? `${toolData.chatSuccessRate.ratePercent}%` : undefined}
            meta={
              toolData.chatSuccessRate.status === 'ok'
                ? 'Successful ÷ total Copilot Chat requests'
                : `No data — ${toolData.chatSuccessRate.reason}`
            }
            info={METRIC_INFO.chatSuccessRate}
            size="sm"
          />
          <EfficiencyGauge
            label="Reporting coverage"
            value={coverage.coveragePercent}
            valueLabel={`${coverage.coveragePercent}%`}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices, last 48h`}
            info={METRIC_INFO.reportingCoverage}
            size="sm"
          />
        </div>
        <p className="mt-3 text-xs text-text-muted">
          <span className="font-medium text-text-default">Not current spend.</span> GitHub bills Copilot in AI credits with an
          included allowance; AIU is a legacy unit and cannot be converted to credits, so this estimate is left out of the
          Executive and All Tools spend figures. Use GitHub's billing reports for Copilot spend.{' '}
          <span className="font-medium text-text-default">Estimate basis:</span> {toolData.premiumQuota.indicativeCostSource}
          . Excludes seat licences, allowances, discounts, taxes, and contract-specific pricing.
        </p>
      </section>

      <section>
        <SectionHeading title="Trends & breakdown" hint="Click any model row to drill down" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <ChartCard
            title="Engagement trend"
            caption="Chat requests and responses per day, corrected to max() per device"
            info={METRIC_INFO.engagementTrend}
            height="h-96"
            className="lg:col-span-8"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={groupCopilotTimeSeries(toolData.timeSeries)}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="date" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="chatRequests" fill="#0058AB" name="Chat requests" />
                <Bar dataKey="chatResponses" fill="#00B4D8" name="Chat responses" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Session activity by source"
            caption="Requests, sessions, and summed active minutes; — means no source metrics were reported"
            height="h-96"
            className="min-w-0 lg:col-span-4"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                  <tr>
                    <th scope="col" className="pb-2.5 pr-4">Source</th>
                    <th scope="col" className="pb-2.5 px-2 text-right">Requests</th>
                    <th scope="col" className="pb-2.5 px-2 text-right">Sessions</th>
                    <th scope="col" className="pb-2.5 pl-2 text-right">Active min</th>
                  </tr>
                </thead>
                <tbody>
                  <SessionActivityRow label="VS Code chat" activity={sessionActivity.vscode} />
                  <SessionActivityRow label="Copilot CLI" activity={sessionActivity.cli} />
                  <SessionActivityRow label="Visual Studio Copilot" activity={sessionActivity.visualStudio} />
                </tbody>
              </table>
            </div>
          </ChartCard>

          <IndicativeCostChart
            byModel={toolData.premiumQuota.byModel}
            currency={toolData.premiumQuota.currency}
            indicativeCostPerAiu={toolData.premiumQuota.indicativeCostPerAiu}
            formatCurrency={formatCurrency}
            info={METRIC_INFO.premiumQuotaByModel}
            className="lg:col-span-6"
          />

          <CostEfficiencyChart
            byModel={toolData.premiumQuota.byModel}
            currency={toolData.premiumQuota.currency}
            formatCurrency={formatCurrency}
            info={METRIC_INFO.costEfficiency}
            className="lg:col-span-6"
          />

          <ChartCard
            title="Context weight"
            caption={`Input vs output tokens${toolData.contextWeight.ratio > 0 ? ` — ${toolData.contextWeight.ratio}:1 means context, not generation, drives spend` : ''}`}
            info={METRIC_INFO.contextWeight}
            className="lg:col-span-12"
          >
            <ContextWeightChart contextWeight={toolData.contextWeight} />
          </ChartCard>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <ChartCard
          title="Usage by model"
          caption="De-duplicated completion/chat events per model"
          info={METRIC_INFO.usageByModel}
          height="h-80"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={toolData.byModel}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
              <XAxis dataKey="model" {...chartXAxisTickProps} />
              <YAxis {...chartAxisTickProps} />
              <Tooltip {...chartTooltipProps} />
              <Bar dataKey="events" fill="#0058AB" name="Events" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Usage by editor / IDE"
          caption="Active devices and total events per editor"
          info={METRIC_INFO.usageByEditor}
          height="h-80"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={toolData.byEditor}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
              <XAxis dataKey="editor" {...chartXAxisTickProps} />
              <YAxis {...chartAxisTickProps} />
              <Tooltip {...chartTooltipProps} />
              <Bar dataKey="activeDevices" fill="#06D6A0" name="Active devices" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      <section>
        <DrillDownTable
          byModel={toolData.byModel}
          premiumQuotaByModel={toolData.premiumQuota.byModel}
          breakdown={toolData.modelDeveloperBreakdown}
          currency={toolData.premiumQuota.currency}
          indicativeCostPerAiu={toolData.premiumQuota.indicativeCostPerAiu}
          info={METRIC_INFO.drillDown}
        />
      </section>

      <section>
        <RepositoryBreakdownTable byBranch={toolData.byBranch} info={METRIC_INFO.byBranch} />
      </section>
    </>
  );
}

type SessionSourceActivity = { requests: number; sessions: number; activeMinutes: number; available: boolean };

function SessionActivityRow({ label, activity }: { label: string; activity: SessionSourceActivity }) {
  const display = (value: number) => (activity.available ? value.toLocaleString() : '—');
  return (
    <tr className="border-b border-border last:border-0">
      <th scope="row" className="py-3 pr-4 font-medium text-text-default">{label}</th>
      <td className="py-3 px-2 text-right tabular-nums">{display(activity.requests)}</td>
      <td className="py-3 px-2 text-right tabular-nums">{display(activity.sessions)}</td>
      <td className="py-3 pl-2 text-right tabular-nums">{display(activity.activeMinutes)}</td>
    </tr>
  );
}

function summarizeCopilotSessionActivity(timeSeries: CopilotToolSummary['timeSeries']) {
  const summarizeSource = (metricNames: readonly string[]): SessionSourceActivity => {
    const available = timeSeries.some((point) => metricNames.includes(point.metricName));
    const total = (metricName: string) =>
      timeSeries.filter((point) => point.metricName === metricName).reduce((sum, point) => sum + point.value, 0);

    return {
      requests: total(metricNames[0]),
      sessions: total(metricNames[1]),
      activeMinutes: total(metricNames[2]),
      available,
    };
  };

  return {
    vscode: summarizeSource([
      COPILOT_SESSION_METRIC_NAMES.vscodeRequests,
      COPILOT_SESSION_METRIC_NAMES.vscodeSessions,
      COPILOT_SESSION_METRIC_NAMES.vscodeActiveMinutes,
    ]),
    cli: summarizeSource([
      COPILOT_SESSION_METRIC_NAMES.cliRequests,
      COPILOT_SESSION_METRIC_NAMES.cliSessions,
      COPILOT_SESSION_METRIC_NAMES.cliActiveMinutes,
    ]),
    visualStudio: summarizeSource([
      COPILOT_SESSION_METRIC_NAMES.visualStudioRequests,
      COPILOT_SESSION_METRIC_NAMES.visualStudioSessions,
      COPILOT_SESSION_METRIC_NAMES.visualStudioActiveMinutes,
    ]),
  };
}

function groupCopilotTimeSeries(timeSeries: { date: string; metricName: string; value: number }[]) {
  const byDate = new Map<string, { date: string; chatRequests: number; chatResponses: number }>();
  for (const point of timeSeries) {
    const row = byDate.get(point.date) ?? { date: point.date, chatRequests: 0, chatResponses: 0 };
    if (point.metricName === 'copilot.chat.requests') row.chatRequests = point.value;
    if (point.metricName === 'copilot.chat.responses') row.chatResponses = point.value;
    byDate.set(point.date, row);
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}
